/**
 * Protected route wrapper that shows a loading spinner while auth
 * initializes, then redirects to login if not authenticated.
 *
 * (Monday Item ID: 11501542831)
 */

import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Loading spinner shown during auth initialization.
 */
function LoadingSpinner() {
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <svg
        className="h-8 w-8 animate-spin text-primary"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isInitializing } = useAuthStore();
  const location = useLocation();

  // The route the operator was bounced from, so Login can return them there
  // after a successful re-login instead of dropping them on /dashboard (#1079).
  //
  // Captured once at mount, and as the three plain strings Login actually reads.
  // Both properties are load-bearing (#1271):
  //
  //  - Stable identity. react-router lists `state` in <Navigate>'s effect
  //    dependencies, so a fresh object literal each render re-fires the
  //    redirect, which re-renders, which re-fires it — a loop with no fixed
  //    point wherever this component stays mounted past the redirect.
  //  - Plain strings, not `location`. Embedding the location object puts the
  //    previous location inside the next one's state, so every pass through
  //    that loop retains its predecessor and the heap grows without bound.
  //
  // Recomputing this per render would also overwrite `from` with /login once
  // the redirect lands, defeating #1079.
  const [redirectState] = useState(() => ({
    from: { pathname: location.pathname, search: location.search, hash: location.hash },
  }));

  if (isInitializing) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={redirectState} replace />;
  }

  return <>{children}</>;
};
